import { Splide } from "@splidejs/splide";
import { Video } from "@splidejs/splide-extension-video";
import { Player } from "@splidejs/splide-extension-video/dist/types/classes/Player";
import { CookieYes } from "../cookie-yes/cookie-yes";
import { Modal } from "../modal/modal";
import { EventHandler, WithDelegateTarget } from "../../utils/event-handler";
import { EVENT_CLICK, EVENT_KEYDOWN } from "../../utils/events";
import { KEY_ENTER } from "../../utils/keyboard";

import "./gallery-modal.scss";

export class GalleryModal extends Modal {
    static #SELECTOR_GALLERY_MODAL = '#gallery-modal';
    #SELECTOR_GALLERY_GROUP = '[data-gallery-group]';
    #SELECTOR_GALLERY_VIDEO = '[data-splide-youtube]';

    #SELECTOR_GALLERY_MODAL_SLIDER = '.gallery-modal-slider';
    #SELECTOR_GALLERY_MODAL_SLIDER_MAIN = `${this.#SELECTOR_GALLERY_MODAL_SLIDER}__main`;
    #SELECTOR_GALLERY_MODAL_SLIDER_THUMBNAILS = `${this.#SELECTOR_GALLERY_MODAL_SLIDER}__thumbnails`;

    #CLASS_NAME_GALLERY_SINGLE = `${this.#SELECTOR_GALLERY_MODAL_SLIDER.slice(1)}--single`;
    #CLASS_NAME_SLIDE_BLOCKED_CONTENT = 'splide__slide--blocked-content';

    #DATASET_GALLERY_GROUP = 'galleryGroup';
    #DATASET_GALLERY_ZOOM_SRC = 'galleryZoomSrc';
    #DATASET_GALLERY_YOUTUBE_ID = 'galleryYoutubeId';
    #DATASET_GALLERY_THUMB_SRC = 'galleryThumbSrc';

    #SPLIDE_SELECTOR_LIST = '.splide__list';
    #SPLIDE_CLASS_NAME_INITIALIZED = 'is-initialized';
    #SPLIDE_CLASS_NAME_SLIDE = 'splide__slide';
    #SPLIDE_DATASET_SRC = 'splideLazy';
    #SPLIDE_DATASET_YOUTUBE = 'splideYoutube';

    #sliderParent: HTMLElement;
    #slider: Splide;
    #mainSliderRoot: HTMLElement;
    #mainList: HTMLElement;
    #thumbnailSlider: Splide;
    #thumbnailSliderRoot: HTMLElement;
    #thumbnailList: HTMLElement;

    constructor() {
        super(GalleryModal.#SELECTOR_GALLERY_MODAL);

        this.#initElements();
        this.#initEvents();
    }

    #initElements(): void {
        this.#sliderParent = this.modal.querySelector(this.#SELECTOR_GALLERY_MODAL_SLIDER);

        this.#mainSliderRoot = this.modal.querySelector(this.#SELECTOR_GALLERY_MODAL_SLIDER_MAIN);
        this.#mainList = this.#mainSliderRoot.querySelector(this.#SPLIDE_SELECTOR_LIST);

        this.#thumbnailSliderRoot = this.modal.querySelector(this.#SELECTOR_GALLERY_MODAL_SLIDER_THUMBNAILS);
        this.#thumbnailList = this.#thumbnailSliderRoot.querySelector(this.#SPLIDE_SELECTOR_LIST);
    }

    #initEvents(): void {
        EventHandler.on(document, EVENT_CLICK, this.#SELECTOR_GALLERY_GROUP, (event: WithDelegateTarget<MouseEvent>) => {
            this.#openGallery(<HTMLElement>event.delegateTarget);
        });

        EventHandler.on(document, EVENT_KEYDOWN, this.#SELECTOR_GALLERY_GROUP, (event: WithDelegateTarget<KeyboardEvent>) => {
            if (event.key !== KEY_ENTER) {
                return;
            }

            this.#openGallery(<HTMLElement>event.delegateTarget);
        });

        EventHandler.on(this.modal, Modal.EVENT_MODAL_OPENED, () => {
            this.#thumbnailSlider.on('ready', () => {
                this.#thumbnailSlider.Components.Slides.get(true)[this.#thumbnailSlider.index].slide.focus();
            });

            this.#slider.on('video:ended', (player: Player) => {
                player.pause();
            });

            this.#slider.on('destroy', () => {
                this.#slider.root.classList.remove(this.#SPLIDE_CLASS_NAME_INITIALIZED);
            });

            this.#thumbnailSlider.on('destroy', () => {
                this.#thumbnailSlider.root.classList.remove(this.#SPLIDE_CLASS_NAME_INITIALIZED);
            });

            const videoSlides = Array.from(this.#mainList.querySelectorAll<HTMLElement>(this.#SELECTOR_GALLERY_VIDEO));

            this.#slider.mount({
                ...(videoSlides.length ? { Video } : {})
            });
            this.#thumbnailSlider.mount();

            if (videoSlides.length) {
                if (!CookieYes.isProviderAllowed(CookieYes.PROVIDER_YOUTUBE)) {
                    this.#slider.Components.Video.disable(true);

                    videoSlides.forEach((slide) => {
                        slide.classList.add(this.#CLASS_NAME_SLIDE_BLOCKED_CONTENT);

                        CookieYes.blockContentByProvider(slide, CookieYes.PROVIDER_YOUTUBE);
                    });

                    CookieYes.onceProviderAllowed(CookieYes.PROVIDER_YOUTUBE, () => {
                        this.#slider.Components.Video.disable(false);

                        videoSlides.forEach((slide) => {
                            slide.classList.remove(this.#CLASS_NAME_SLIDE_BLOCKED_CONTENT);
                        });
                    });
                }
            }
        });

        EventHandler.on(this.modal, Modal.EVENT_MODAL_CLOSED, () => {
            this.#slider.destroy(true);
            this.#thumbnailSlider.destroy(true);

            this.#mainList.replaceChildren();
            this.#thumbnailList.replaceChildren();
        });
    }

    #openGallery(slide: HTMLElement): void {
        this.#prepareGallery(
            document.querySelectorAll<HTMLElement>(`${this.#SELECTOR_GALLERY_GROUP}[data-gallery-group="${slide.dataset[this.#DATASET_GALLERY_GROUP]}"]`),
            slide
        );

        this.open();
    }

    #prepareGallery(slides: NodeListOf<HTMLElement>, slide?: HTMLElement): void {
        const items = document.createDocumentFragment();
        const thumbs = document.createDocumentFragment();

        let start = 0;

        Array.from(slides).forEach((item, i) => {
            items.append(this.#createItemFrom(item));
            thumbs.append(this.#createThumbFrom(item));

            if (item === slide) {
                start = i;
            }
        });

        this.#mainList.append(items);
        this.#thumbnailList.append(thumbs);

        this.#slider = new Splide(this.#mainSliderRoot, {
            start,
            arrows: false,
            gap: 40,
            pagination: false,
            lazyLoad: 'nearby',
            preloadPages: 2,

            video: {
                volume: 0.5,
            },
        });

        this.#thumbnailSlider = new Splide(this.#thumbnailSliderRoot, {
            // rewind: true,
            autoWidth: true,
            start,
            focus: 'center',
            gap: 8,
            pagination: false,
            keyboard: true,
            isNavigation: true,
            mediaQuery: 'min',
            breakpoints: {
                768: {
                    gap: 16,
                }
            },
        });

        this.#slider.sync(this.#thumbnailSlider);

        this.#sliderParent.classList.toggle(this.#CLASS_NAME_GALLERY_SINGLE, slides.length === 1);
    }

    #createItemFrom(slide: HTMLElement): HTMLElement {
        const item = document.createElement('li');
        item.classList.add(this.#SPLIDE_CLASS_NAME_SLIDE);

        const image = document.createElement('img');
        image.dataset[this.#SPLIDE_DATASET_SRC] = slide.dataset[this.#DATASET_GALLERY_ZOOM_SRC];
        item.append(image);

        if (slide.dataset[this.#DATASET_GALLERY_YOUTUBE_ID]) {
            item.dataset[this.#SPLIDE_DATASET_YOUTUBE] = slide.dataset[this.#DATASET_GALLERY_YOUTUBE_ID];
        }

        return item;
    }

    #createThumbFrom(slide: HTMLElement): HTMLElement {
        const thumb = document.createElement('li');
        const image = document.createElement('img');

        image.src = slide.dataset[this.#DATASET_GALLERY_THUMB_SRC];

        thumb.classList.add(this.#SPLIDE_CLASS_NAME_SLIDE);
        thumb.append(image);

        return thumb;
    }
}
